import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { MRT_ColumnFiltersState, MRT_SortingState } from 'mantine-react-table';

import queryString from 'api/utils/queryStringBuilder';
import { getRequest as get, postRequest as post } from 'api/utils/HttpMethods';

import {
  convertFiltersToQueryString,
  convertGlobalFilterToQueryString,
  convertSortingToQueryString,
} from 'utils/filterUtils';

import {
  CountParams,
  CountReturnTypes,
  hasSiteID,
  PaginatedResult,
  PaginationParams,
  StatusesCountResult,
  useCountQuery,
  useGetQuery,
  useMutationQuery,
  usePaginatedQuery,
} from 'api/utils/queries';

import {
  claimQueryRecordSchema,
  type ClaimQueryRecord,
} from '@billing/validation/claimQuery';

const isClaimQueryRecord = (obj: unknown): obj is ClaimQueryRecord =>
  claimQueryRecordSchema.safeParse(obj).success;

export type ClaimQueryPaginatedResult = PaginatedResult<ClaimQueryRecord>;

export const claimQueryResourceKeys = {
  all: ['claim-query'],
  get: (id: string) => [...claimQueryResourceKeys.all, id],
  paginated: (
    siteID: string | string[],
    page: number,
    pageSize: number,
    filters?: MRT_ColumnFiltersState,
    globalFilter?: string,
    sorting?: MRT_SortingState
  ) => [
    ...claimQueryResourceKeys.all,
    'page',
    siteID,
    page,
    pageSize,
    filters,
    globalFilter,
    sorting,
  ],
  counts: (
    siteID: string | string[],
    filters?: MRT_ColumnFiltersState,
    globalFilter?: string
  ) => [...claimQueryResourceKeys.all, 'counts', siteID, filters, globalFilter],
};

const getClaimQuery = async (id: string): Promise<ClaimQueryRecord | null> =>
  await get<ClaimQueryRecord>(
    'QBilling',
    `/resource/claim-query${queryString({ id })}`
  );

const getPaginatedClaimQueriesBySite = async ({
  siteID,
  page,
  pageSize,
  filters,
  globalFilter,
  sorting,
}: PaginationParams): Promise<ClaimQueryPaginatedResult | null> => {
  const filtersString = convertFiltersToQueryString(filters);
  const globalFilterString = convertGlobalFilterToQueryString(globalFilter);
  const sortingString = convertSortingToQueryString(sorting);

  return await get<ClaimQueryPaginatedResult>(
    'QBilling',
    `/resource/claim-query/site/page${queryString({
      siteID,
      page,
      pageSize,
      filters: filtersString,
      globalFilter: globalFilterString,
      sorting: sortingString,
    })}`
  );
};

export const pollClaimQueryStatus = async ({
  id,
}: {
  id: string;
}): Promise<ClaimQueryRecord | null> =>
  await post<ClaimQueryRecord, { id: string }>(
    'QBilling',
    '/resource/claim-query/poll',
    {
      id,
    }
  );

const getClaimQueryStatusesCount = async ({
  siteID,
  filters,
  globalFilter,
}: CountParams): Promise<StatusesCountResult> => {
  const filtersString = convertFiltersToQueryString(filters);
  const globalFilterString = convertGlobalFilterToQueryString(globalFilter);

  return await get<StatusesCountResult>(
    'QBilling',
    `/resource/claim-query/status/count${queryString({
      siteID,
      filters: filtersString,
      globalFilter: globalFilterString,
    })}`
  );
};

export const useGetClaimQuery = ({
  id,
  enabled = true,
  displayNotification = false,
}: {
  id: string;
  enabled?: boolean;
  displayNotification?: boolean;
}): UseQueryResult<ClaimQueryRecord, Error> =>
  useGetQuery<ClaimQueryRecord>({
    queryKey: claimQueryResourceKeys.get(id),
    fetchFunction: getClaimQuery,
    isEntity: isClaimQueryRecord,
    functionName: 'getClaimQuery',
    enabled: !!id && enabled,
    params: id,
    displayNotification,
  });

export const usePaginateClaimQueriesBySite = (
  siteID: string | string[],
  page: number,
  pageSize: number,
  filters?: MRT_ColumnFiltersState,
  globalFilter?: string,
  sorting?: MRT_SortingState
): UseQueryResult<ClaimQueryPaginatedResult, Error> =>
  usePaginatedQuery<ClaimQueryRecord, ClaimQueryPaginatedResult>({
    queryKey: claimQueryResourceKeys.paginated(
      siteID,
      page,
      pageSize,
      filters,
      globalFilter,
      sorting
    ),
    fetchFunction: getPaginatedClaimQueriesBySite,
    isEntity: isClaimQueryRecord,
    functionName: 'getPaginatedClaimQueriesBySite',
    params: {
      siteID,
      page,
      pageSize,
      filters,
      globalFilter,
      sorting,
    },
    enabled: hasSiteID(siteID),
  });

export const usePollClaimQuery = (): UseMutationResult<
  ClaimQueryRecord,
  Error
> =>
  useMutationQuery<ClaimQueryRecord>({
    mutationFn: pollClaimQueryStatus,
    successMessage: 'Query Refresh Request Logged',
    errorMessage: 'Query Status Refresh Failed',
    queryKey: claimQueryResourceKeys.all as string[],
  });

export const useGetClaimQueryStatusesCount = (
  siteID: string | string[],
  filters?: MRT_ColumnFiltersState,
  globalFilter?: string
): UseQueryResult<CountReturnTypes, Error> =>
  useCountQuery({
    queryKey: claimQueryResourceKeys.counts(siteID, filters, globalFilter),
    functionName: 'getClaimQueryStatusesCount',
    enabled: hasSiteID(siteID),
    params: {
      siteID,
      filters,
      globalFilter,
    },
    fetchFunction: getClaimQueryStatusesCount,
  });
